import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseDraggablePanelArgs {
  open: boolean;
  /** Initial position on desktop viewports (≥768px wide). */
  desktopInitialPosition: { x: number; y: number };
  /** Initial position on mobile viewports (<768px wide). Defaults to {x: 8, y: 60}. */
  mobileInitialPosition?: { x: number; y: number };
}

export interface DraggablePanelApi {
  position: { x: number; y: number };
  folded: boolean;
  setFolded: (folded: boolean) => void;
  panelRef: React.RefObject<HTMLDivElement>;
  dragHandlers: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
  };
}

/**
 * Drag + fold + viewport-aware initial-position behavior shared by floating
 * panel dialogs (HelpDialog, AiJsonDialog, …). When `open` flips true the
 * panel resets to the configured initial position and unfolds.
 *
 * Mouse and touch are both supported; pointermove/up listeners are wired on
 * window so dragging keeps tracking even when the cursor leaves the title
 * bar. Touch listeners use `{ passive: false }` so we can call
 * `preventDefault()` to stop the page from scrolling under the finger.
 */
export function useDraggablePanel({
  open,
  desktopInitialPosition,
  mobileInitialPosition = { x: 8, y: 60 },
}: UseDraggablePanelArgs): DraggablePanelApi {
  const [folded, setFolded] = useState(false);
  const [position, setPosition] = useState(desktopInitialPosition);
  const dragRef = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const isMobile = window.innerWidth < 768;
    setPosition(isMobile ? mobileInitialPosition : desktopInitialPosition);
    setFolded(false);
    // Initial-position config is captured once when open flips — re-running
    // on every render of changed config objects would jump the panel back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, posX: position.x, posY: position.y };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPosition({
        x: dragRef.current.posX + ev.clientX - dragRef.current.startX,
        y: dragRef.current.posY + ev.clientY - dragRef.current.startY,
      });
    };
    const handleMouseUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [position]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    dragRef.current = { startX: touch.clientX, startY: touch.clientY, posX: position.x, posY: position.y };
    const handleTouchMove = (ev: TouchEvent) => {
      if (!dragRef.current || ev.touches.length !== 1) return;
      ev.preventDefault();
      setPosition({
        x: dragRef.current.posX + ev.touches[0].clientX - dragRef.current.startX,
        y: dragRef.current.posY + ev.touches[0].clientY - dragRef.current.startY,
      });
    };
    const handleTouchEnd = () => {
      dragRef.current = null;
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
  }, [position]);

  return {
    position,
    folded,
    setFolded,
    panelRef,
    dragHandlers: { onMouseDown: handleMouseDown, onTouchStart: handleTouchStart },
  };
}
